import { useMemo } from 'react'
import { create } from 'zustand'
import type { OddsFormat } from '../utils/odds'
import type { HostFormatSink } from '../providers/bettingProviderTypes'

const CURRENCY_CODE_PATTERN = /^[A-Z]{3}$/

/**
 * How the host sportsbook shows numbers to this player: its locale, the
 * currency of its offering and the odds format the player picked. One
 * organization can span several of each (RushBet Mexico is MXN and American
 * odds, RushBet Peru PEN and decimal), so they come from the host, never from
 * the organization: Kambi's page config (`window._kc`) when the widget mounts,
 * then the client's own answers over WAPI (`ClientConfig`, `ClientOddsFormat`),
 * which also report a player switching odds format.
 */
export interface HostFormat {
  /** BCP 47 locale such as `es-MX`. */
  locale?: string
  /** ISO 4217 code such as `MXN`. */
  currency?: string
  oddsFormat?: OddsFormat
}

interface HostFormatState {
  /** What the Kambi client reported over WAPI; wins over `_kc`. */
  reported: HostFormat
  applyClientConfig: (data: unknown) => void
  applyOddsFormat: (value: unknown) => void
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return null
  return value as Record<string, unknown>
}

/** Kambi writes locales as `es_MX`; Intl wants `es-MX`. */
export function normalizeHostLocale(value: unknown): string | undefined {
  if (typeof value !== 'string' || !value.trim()) return undefined
  try {
    return Intl.getCanonicalLocales(value.trim().replaceAll('_', '-'))[0]
  } catch {
    return undefined
  }
}

export function normalizeHostCurrency(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  const currency = value.trim().toUpperCase()
  return CURRENCY_CODE_PATTERN.test(currency) ? currency : undefined
}

/** Fractional odds are shown as decimal: the cards carry no fractional price. */
export function normalizeHostOddsFormat(value: unknown): OddsFormat | undefined {
  if (typeof value !== 'string') return undefined
  const format = value.trim().toLowerCase()
  if (format === 'american') return 'american'
  if (format === 'decimal' || format === 'fractional') return 'decimal'
  return undefined
}

function hostFormatFrom(config: unknown): HostFormat {
  const record = asRecord(config)
  if (!record) return {}
  const format: HostFormat = {}
  const locale = normalizeHostLocale(record.locale)
  const currency = normalizeHostCurrency(record.currency)
  const oddsFormat = normalizeHostOddsFormat(record.oddsFormat)
  if (locale) format.locale = locale
  if (currency) format.currency = currency
  if (oddsFormat) format.oddsFormat = oddsFormat
  return format
}

/** The page's Kambi config, read when asked: operators set it before Kambi boots. */
export function readKambiPageFormat(): HostFormat {
  if (typeof window === 'undefined') return {}
  return hostFormatFrom((window as Window & { _kc?: unknown })._kc)
}

export const useHostFormatStore = create<HostFormatState>((set) => ({
  reported: {},
  applyClientConfig: (data) => {
    const format = hostFormatFrom(data)
    if (Object.keys(format).length === 0) return
    set((state) => ({ reported: { ...state.reported, ...format } }))
  },
  applyOddsFormat: (value) => {
    const oddsFormat = normalizeHostOddsFormat(value)
    if (!oddsFormat) return
    set((state) => (state.reported.oddsFormat === oddsFormat
      ? state
      : { reported: { ...state.reported, oddsFormat } }))
  },
}))

/** What the WAPI provider reports into (WapiBettingProvider's hostFormat). */
export const hostFormatSink: HostFormatSink = {
  applyClientConfig: (data) => useHostFormatStore.getState().applyClientConfig(data),
  applyOddsFormat: (value) => useHostFormatStore.getState().applyOddsFormat(value),
}

export function useHostFormat(): HostFormat {
  const reported = useHostFormatStore((state) => state.reported)
  return useMemo(() => ({ ...readKambiPageFormat(), ...reported }), [reported])
}

/** American unless the host says otherwise: every US book shows American. */
export function useOddsFormat(): OddsFormat {
  return useHostFormat().oddsFormat ?? 'american'
}
